import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Printer, AlertTriangle, CheckCircle2, RefreshCw, Wifi, WifiOff, Zap } from "lucide-react";
import api, { apiError } from "../../lib/api";

// Stats d'impression thermique par restaurant (30 j par défaut).
// Aide l'admin à repérer les imprimantes défectueuses ou mal appairées à distance.
export default function PrintStatsAdmin() {
  const [data, setData] = useState(null);
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const [stats, liveRes] = await Promise.all([
        api.get(`/admin/print-stats?days=${d}`).then(r => r.data),
        api.get(`/admin/printer-live-status`).then(r => r.data).catch(() => null),
      ]);
      setData(stats);
      setLive(liveRes);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // Auto-refresh live status toutes les 60 s pour rester à jour
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/admin/printer-live-status`).then(x => x.data);
        setLive(r);
      } catch {}
    }, 60000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  if (loading && !data) return null;

  const g = data?.global || { total: 0, success: 0, failed: 0, success_rate: 0 };
  const items = data?.items || [];

  return (
    <div className="mt-6 space-y-4" data-testid="admin-print-stats">
      {/* WIDGET LIVE STATUS — imprimantes en temps réel par restaurateur */}
      {live && (
        <div className="bg-white rounded-2xl border-2 border-[#0EA5E9]/40 p-5" data-testid="printer-live-status">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-[#0EA5E9]" />
              <h3 className="font-head font-black text-lg text-[#1A1A1A]">Statut imprimantes — Temps réel</h3>
            </div>
            <span className="text-[10px] text-[#75807A]">MAJ auto toutes les 60 s</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <StatBox label="🟢 En ligne" value={live.counts?.online || 0} tone="green" />
            <StatBox label="🟡 En veille" value={live.counts?.idle || 0} tone="amber" />
            <StatBox label="🔴 En erreur" value={live.counts?.error || 0} tone="red" />
            <StatBox label="⚫ Hors ligne" value={live.counts?.offline || 0} />
            <StatBox label="⚪ Jamais appairée" value={live.counts?.never_paired || 0} />
          </div>
          {(live.items || []).length > 0 && (
            <div className="mt-3 space-y-1.5 max-h-[340px] overflow-y-auto">
              {(live.items || []).map((r) => <LiveRow key={r.resto_user_id} row={r} />)}
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-2xl border-2 border-[#F26419]/40 p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Printer className="w-5 h-5 text-[#F26419]" />
            <h3 className="font-head font-black text-lg text-[#1A1A1A]">Stats impression thermique</h3>
          </div>
          <div className="flex items-center gap-2">
            <select value={days} onChange={(e) => { setDays(Number(e.target.value)); load(Number(e.target.value)); }}
              data-testid="print-stats-range"
              className="h-9 px-3 rounded-lg border border-[#E2DFD8] text-sm bg-white">
              <option value="7">7 jours</option>
              <option value="30">30 jours</option>
              <option value="90">90 jours</option>
            </select>
            <button data-testid="print-stats-refresh" onClick={() => load()} className="h-9 px-3 rounded-lg bg-[#F0EBE1] hover:bg-[#E2DFD8] text-[#1A1A1A] font-bold text-xs inline-flex items-center gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" /> Actualiser
            </button>
          </div>
        </div>
        <p className="text-xs text-[#75807A] mt-1">Repérez rapidement les imprimantes défectueuses ou déconnectées chez vos restaurateurs partenaires.</p>

        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatBox label="Impressions" value={g.total} />
          <StatBox label="Réussies" value={g.success} tone="green" />
          <StatBox label="Échecs" value={g.failed} tone="red" />
          <StatBox label="Taux succès" value={`${g.success_rate}%`} tone={g.success_rate >= 95 ? "green" : g.success_rate >= 80 ? "amber" : "red"} />
        </div>
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl bg-white border border-dashed border-[#E2DFD8] p-8 text-center text-sm text-[#75807A]">
          Aucune impression enregistrée sur cette période.
        </div>
      ) : items.map((r) => <RestoRow key={r.resto_user_id} row={r} />)}
    </div>
  );
}


function StatBox({ label, value, tone }) {
  const cls = tone === "green" ? "bg-[#DCFCE7] text-[#166534] border-[#16a34a]/30" :
              tone === "red" ? "bg-[#FEE2E2] text-[#B91C1C] border-[#B91C1C]/30" :
              tone === "amber" ? "bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]/30" :
              "bg-white text-[#1A1A1A] border-[#E2DFD8]";
  return (
    <div className={`rounded-xl border-2 p-3 ${cls}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-black mt-0.5">{value}</div>
    </div>
  );
}


function LiveRow({ row }) {
  const s = row.status;
  const conf = {
    online:       { color: "bg-[#16a34a] text-white", label: "En ligne", Icon: Wifi },
    idle:         { color: "bg-[#F59E0B] text-white", label: "En veille", Icon: Wifi },
    error:        { color: "bg-[#B91C1C] text-white", label: "En erreur", Icon: AlertTriangle },
    offline:      { color: "bg-[#3B423E] text-white", label: "Hors ligne", Icon: WifiOff },
    never_paired: { color: "bg-white text-[#75807A] border border-[#E2DFD8]", label: "Jamais appairée", Icon: Printer },
  }[s] || { color: "bg-[#F0EBE1] text-[#75807A]", label: s, Icon: Printer };
  const Icon = conf.Icon;
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg bg-[#FAF7F1] px-3 py-2 text-xs" data-testid={`live-row-${row.resto_user_id}`}>
      <div className="min-w-0 flex-1">
        <div className="font-bold text-[#1A1A1A] truncate">{row.business_name || row.email || "—"}</div>
        <div className="text-[#75807A] truncate">
          {row.printer || "—"} · {row.last_at ? `Dernier ticket : ${new Date(row.last_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : "Aucun ticket imprimé"}
        </div>
        {s === "error" && row.last_error && (
          <div className="text-[#B91C1C] text-[10px] mt-0.5 truncate">↳ {row.last_error}</div>
        )}
      </div>
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase shrink-0 ${conf.color}`}>
        <Icon className="w-3 h-3" /> {conf.label}
      </span>
    </div>
  );
}


function RestoRow({ row }) {
  const rate = row.success_rate;
  const isDegraded = rate < 80 && row.total >= 3;
  const isBad = rate < 50 && row.total >= 3;
  const cls = isBad ? "border-[#B91C1C] bg-[#FEE2E2]/30" : isDegraded ? "border-[#F59E0B] bg-[#FEF3C7]/40" : "border-[#E2DFD8] bg-white";
  return (
    <div className={`rounded-2xl border-2 p-4 ${cls}`} data-testid={`resto-row-${row.resto_user_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-head font-bold text-[#1A1A1A]">{row.business_name || row.email || "—"}</div>
            {isBad ? <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase bg-[#B91C1C] text-white inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Imprimante en panne ?</span> :
             isDegraded ? <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase bg-[#F59E0B] text-white">Instable</span> :
             row.total >= 3 && <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase bg-[#DCFCE7] text-[#166534] inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> OK</span>}
          </div>
          <div className="text-xs text-[#75807A] mt-0.5">{row.printer || "—"} · Dernière impression : {row.last_at ? new Date(row.last_at).toLocaleString("fr-FR") : "—"}</div>
          {row.recent_errors?.length > 0 && (
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer text-[#B91C1C] font-bold">Voir les {row.recent_errors.length} dernière(s) erreur(s)</summary>
              <ul className="mt-1 space-y-0.5">
                {row.recent_errors.map((e, i) => (
                  <li key={i} className="pl-3 border-l-2 border-[#B91C1C]/30">
                    <span className="text-[#75807A]">{new Date(e.at).toLocaleString("fr-FR")}</span> — {e.err || "erreur inconnue"}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2 shrink-0 text-center">
          <div><div className="text-[10px] text-[#75807A] font-bold uppercase">Total</div><div className="text-lg font-black text-[#1A1A1A]">{row.total}</div></div>
          <div><div className="text-[10px] text-[#75807A] font-bold uppercase">Échecs</div><div className="text-lg font-black text-[#B91C1C]">{row.failed}</div></div>
          <div><div className="text-[10px] text-[#75807A] font-bold uppercase">Taux</div><div className={`text-lg font-black ${rate >= 95 ? "text-[#16a34a]" : rate >= 80 ? "text-[#F59E0B]" : "text-[#B91C1C]"}`}>{rate}%</div></div>
        </div>
      </div>
    </div>
  );
}
